import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  TemplateRef,
  computed,
  viewChild,
  booleanAttribute,
  signal,
  output,
  InputSignalWithTransform,
  model,
} from '@angular/core';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';

import { PaginationRequest } from '@rp/shared/models';
import { TranslateModule } from '@ngx-translate/core';

import { CellMobile } from './model';
import { CustomMatPaginatorIntl } from '../table/intl/custom-paginator.intl';
import { TablePlaceholderComponent } from '../table-placeholder';

@Component({
  selector: 'rp-table-mobile',
  standalone: true,
  templateUrl: './table-mobile.component.html',
  styleUrl: './table-mobile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TablePlaceholderComponent,
    MatTableModule,
    MatPaginatorModule,
    NgTemplateOutlet,
    TranslateModule,
  ],
  providers: [{ provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl }],
})
export class TableMobileComponent<T> {
  paginator = viewChild(MatPaginator);

  template = input.required<TemplateRef<unknown>>();
  data = input.required<T[]>();
  itemsLength = input.required<number>();
  pageSizeOptions = input<number[]>([10, 20, 50]);
  defaultPageSize = model<number>(10);
  showFirstLastButtons = input(true, {
    transform: booleanAttribute,
  });
  hidePageSize = input(true, {
    transform: booleanAttribute,
  });
  readonly titlePlaceholder: InputSignalWithTransform<string, string> = input<string, string>(
    'general.placeholders.noItemsPlaceholderTitle',
    {
      transform: (value: string) => value || 'general.placeholders.noItemsPlaceholderTitle',
    },
  );
  readonly subtitlePlaceholder: InputSignalWithTransform<string, string> = input<string, string>(
    'general.placeholders.noItemsPlaceholderSubTitle',
    {
      transform: (value: string) => value || 'general.placeholders.noItemsPlaceholderSubTitle',
    },
  );

  pagination = output<PaginationRequest>();

  pageSize = signal<number>(this.defaultPageSize());
  pageIndex = signal<number>(0);

  dataSource = computed(
    () => new MatTableDataSource<CellMobile<T>>(this.data().map(data => ({ cell: data }))),
  );

  displayedColumns: string[] = ['cell'];

  onPageChange(pageEvent: PageEvent): void {
    this.pageSize.set(pageEvent.pageSize);
    this.pageIndex.set(pageEvent.pageIndex);
    this.pagination.emit({
      page: pageEvent.pageIndex + 1,
      limit: pageEvent.pageSize,
    });
  }

  setDefaultPageSize(pageSize: number): void {
    this.defaultPageSize.set(pageSize);
  }

  setPaginator(pageIndex: number, pageSize: number): void {
    this.pageIndex.set(pageIndex);
    this.pageSize.set(pageSize);
  }
}
